import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Flex, Text, Table, Thead, Tbody, Tr, Th, Td, IconButton, Icon, VStack, Button, useToast
} from '@chakra-ui/react';
import { useDrag, useDrop } from 'react-dnd';
import { CloseIcon, AddIcon, WarningTwoIcon } from '@chakra-ui/icons';
import { useTeamPitchingRotation, PlayerType, fetchTeamExtendedBench } from '../hooks/useLineups';
import {
  attachSimStats,
  buildPitcherSimMap,
  type PitcherSimEvent,
} from '../lib/pitcherSimStats';
import { useMyLeagueStore, isLineupEditingDisabled } from '../store/myLeagueStore';
import { useUserStore } from '../store/userStore';
import { isAdmin } from '../hooks/useUser';

const ItemTypes = { PITCHER: 'pitcher' };

// DnD Pitcher Row (drag source and drop target)
function PitcherRow({ pitcher, idx, role, gamesKey, movePitcher, onRemove, setDraggedRole, updateRotation, getRotationEntriesForSave, onInsertAt, sp, rp, cp, buildRotationEntries, lineupEditsDisabled = false }: any) {
  const ref = React.useRef(null);
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.PITCHER,
    item: { idx, role, pitcher },
    canDrag: !lineupEditsDisabled,
    collect: (monitor: any) => ({ isDragging: monitor.isDragging() }),
  });

  React.useEffect(() => {
    if (isDragging) setDraggedRole(role);
    else setDraggedRole(null);
  }, [isDragging, role, setDraggedRole]);

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: ItemTypes.PITCHER,
    // Accept drop on any row: same-panel reorder (persist on release) or cross-panel (insert at row)
    canDrop: () => !lineupEditsDisabled,
    hover: (item: any) => {
      if (lineupEditsDisabled) return;
      if (item.role === role && item.idx !== idx) {
        movePitcher(item.idx, idx, role);
        item.idx = idx;
      }
    },
    drop: (item: any) => {
      if (lineupEditsDisabled) return;
      if (item.role !== role) {
        onInsertAt(item, idx, role);
      } else {
        // Same-panel reorder: persist after hover updates; use next tick so ref is current
        setTimeout(() => {
          updateRotation(getRotationEntriesForSave());
        }, 0);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });
  drag(drop(ref));
  const ipNum = Number(pitcher?.stats?.IP ?? 0);
  const hasIp = Number.isFinite(ipNum) && ipNum > 0;
  const gamesValue = gamesKey === 'G' ? (pitcher.stats?.G ?? '') : (pitcher.stats?.GS ?? '');
  return (
    <Tr
      ref={ref}
      opacity={isDragging ? 0.5 : 1}
      bg={isOver && canDrop ? 'blue.700' : undefined}
      transition="background 0.2s"
      style={{ fontStyle: pitcher?.status_until ? 'italic' : undefined }}
    >
      <Td color={pitcher?.status_until ? 'gray.400' : undefined} >
        <Box>
          {(() => {
            const first = pitcher.first_name || pitcher.firstName || '';
            const last = pitcher.last_name || pitcher.lastName || '';
            return first && last ? `${first[0]}. ${last}` : (pitcher.full_name || pitcher.name || last || '');
          })()}
          {pitcher?.status_until && (
            <Text as="i" fontSize="xs" color="gray.400" display="block" >
              Avail: {(() => {
                const d = new Date(pitcher.status_until);
                return `${d.getMonth() + 1}/${d.getDate()}`;
              })()}
            </Text>
          )}
        </Box>
      </Td>
      <Td color={pitcher?.status_until ? 'gray.400' : undefined}>{hasIp ? gamesValue : '-'}</Td>
      <Td color={pitcher?.status_until ? 'gray.400' : undefined}>{hasIp ? (pitcher.stats?.IP ?? '') : '-'}</Td>
      <Td color={pitcher?.status_until ? 'gray.400' : undefined}>{hasIp ? (pitcher.stats?.LW ?? '') : '-'}</Td>
      <Td color={pitcher?.status_until ? 'gray.400' : undefined}>{pitcher.stats?.Rest ?? ''}</Td>
      <Td>
        <IconButton
          aria-label="Remove"
          icon={<CloseIcon />}
          size="xs"
          isDisabled={lineupEditsDisabled}
          onClick={() => onRemove(idx, role)}
        />
      </Td>
    </Tr>
  );
}

// DnD Drop Panel: panel-level drop only when empty; otherwise only rows accept drops (row highlight priority)
function PitcherPanel({ title, pitchers, role, gamesKey, movePitcher, onDropFromOther, onInsertAt, onRemove, onDropFromUnassigned, draggedRole, setDraggedRole, updateRotation, getRotationEntriesForSave, sp, rp, cp, buildRotationEntries, lineupEditsDisabled = false }: any) {
  const [{ isOver }, drop] = useDrop({
    accept: ItemTypes.PITCHER,
    canDrop: () => !lineupEditsDisabled,
    drop: (item: any) => {
      if (lineupEditsDisabled) return;
      if (item.role !== role) {
        if (item.role === 'unassigned' && onDropFromUnassigned) {
          onDropFromUnassigned(item.pitcher, role);
        } else {
          onDropFromOther(item, role);
        }
      } else {
        updateRotation(getRotationEntriesForSave());
      }
    },
    collect: (monitor) => ({ isOver: monitor.isOver() }),
  });
  const isEmpty = pitchers.length === 0;
  const shouldHighlightPanel = isEmpty && isOver && draggedRole !== role;
  return (
    <Box
      ref={isEmpty ? drop : undefined}
      flex={{ base: '1 1 100%', xl: 1 }}
      minW={0}
      mx={{ base: 0, xl: 2 }}
      mb={{ base: 4, xl: 0 }}
      bg={shouldHighlightPanel ? 'blue.700' : 'gray.800'}
      borderRadius="md"
      p={2}
      boxShadow={shouldHighlightPanel ? '0 0 0 3px #3182ce' : undefined}
      transition="background 0.2s, box-shadow 0.2s"
    >
      <Text fontWeight="bold" mb={2}>{title}</Text>
      <Box overflowX="auto">
        <Table size="sm" variant="simple" colorScheme="gray" minW="280px">
          <Thead>
            <Tr>
              <Th>Player</Th>
              <Th>{gamesKey}</Th>
              <Th>IP</Th>
              <Th>LW</Th>
              <Th></Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {pitchers.map((p: PlayerType, idx: number) => (
              <PitcherRow
                key={p.roster_player_id ?? (p as { id?: number }).id ?? `row-${idx}`}
                pitcher={p}
                idx={idx}
                role={role}
                gamesKey={gamesKey}
                movePitcher={movePitcher}
                onRemove={onRemove}
                onInsertAt={onInsertAt}
                getRotationEntriesForSave={getRotationEntriesForSave}
                setDraggedRole={setDraggedRole}
                updateRotation={updateRotation}
                sp={sp}
                rp={rp}
                cp={cp}
                buildRotationEntries={buildRotationEntries}
                lineupEditsDisabled={lineupEditsDisabled}
              />
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
}

function UnassignedPitcherRow({ pitcher, idx, onAssign, lineupEditsDisabled = false }: any) {
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.PITCHER,
    item: { idx, role: 'unassigned', pitcher },
    canDrag: !lineupEditsDisabled,
    collect: (monitor) => ({ isDragging: monitor.isDragging() })
  });
  // Compose 'Last, First' style name
  const last = pitcher.lastName || pitcher.last_name || (pitcher.name || pitcher.full_name || '').split(' ').slice(-1)[0];
  const first = pitcher.firstName || pitcher.first_name || (pitcher.name || pitcher.full_name || '').split(' ').slice(0, -1).join(' ');
  const displayName = last && first ? `${last}, ${first}` : (pitcher.name || pitcher.full_name || last || '');
  const type = (Array.isArray(pitcher.positions) && pitcher.positions.length > 0) ? pitcher.positions[0] : (pitcher.position_1 || '');
  const throws = pitcher.throws || '';
  const resting = pitcher.status_until ? new Date(pitcher.status_until) : '';

  return (
    <Tr ref={drag} opacity={isDragging ? 0.5 : 1} style={{ fontStyle: pitcher?.status_until ? 'italic' : undefined }}>
      <Td color={pitcher?.status_until ? 'gray.400' : undefined} >
        <Box>
          {displayName}
          {resting && (
            <Text fontSize="xs" color="gray.400" display="block">
              Avail: {(() => {
                return `${resting.getMonth() + 1}/${resting.getDate()}`;
              })()}
            </Text>
          )}
        </Box>
      </Td>
      <Td textAlign="center" color={pitcher?.status_until ? 'gray.400' : undefined}>{type}</Td>
      <Td textAlign="center" color={pitcher?.status_until ? 'gray.400' : undefined}>{throws}</Td>
      <Td color={pitcher?.status_until ? 'gray.400' : undefined}>{pitcher.stats?.G ?? pitcher.G ?? ''}</Td>
      <Td color={pitcher?.status_until ? 'gray.400' : undefined}>{pitcher.stats?.GS ?? pitcher.GS ?? ''}</Td>
      <Td color={pitcher?.status_until ? 'gray.400' : undefined}>{pitcher.stats?.IP ?? pitcher.IP ?? ''}</Td>
      <Td color={pitcher?.status_until ? 'gray.400' : undefined}>{pitcher.stats?.LW ?? pitcher.LW ?? ''}</Td>
      <Td color={pitcher?.status_until ? 'gray.400' : undefined}>{pitcher.stats?.Rest ?? pitcher.Rest ?? ''}</Td>
      <Td textAlign="center" w="48px">
        <IconButton aria-label="Add as SP" icon={<AddIcon />} size="xs" isDisabled={lineupEditsDisabled} onClick={() => onAssign(pitcher, 'SP')} />
      </Td>
      <Td textAlign="center" w="48px">
        <IconButton aria-label="Add as SU" icon={<AddIcon />} size="xs" isDisabled={lineupEditsDisabled} onClick={() => onAssign(pitcher, 'RP')} />
      </Td>
      <Td textAlign="center" w="48px">
        <IconButton aria-label="Add as CL" icon={<AddIcon />} size="xs" isDisabled={lineupEditsDisabled} onClick={() => onAssign(pitcher, 'CP')} />
      </Td>
    </Tr>
  );
}

function UnassignedPitchersPanel({ unassigned, onAssign, onDropFromPanel, lineupEditsDisabled = false }: any) {
  const [{ isOver }, drop] = useDrop({
    accept: ItemTypes.PITCHER,
    canDrop: () => !lineupEditsDisabled,
    drop: (item: any, monitor) => {
      if (lineupEditsDisabled) return;
      if (item.role !== 'unassigned' && onDropFromPanel) {
        onDropFromPanel(item);
      }
    },
    collect: (monitor) => {
      const item = monitor.getItem();
      // Only highlight if dragging from a top panel (not from unassigned)
      return {
        isOver: monitor.isOver() && item && item.role !== 'unassigned',
      };
    },
  });
  // Sort by lastName (case-insensitive), fallback to name/full_name if missing
  const sorted = [...(unassigned || [])]
    .filter(Boolean)
    .sort((a, b) => {
      const aLast = (a.lastName || a.last_name || a.name || a.full_name || '').toLowerCase();
      const bLast = (b.lastName || b.last_name || b.name || b.full_name || '').toLowerCase();
      return aLast.localeCompare(bLast);
    });
  return (
    <Box
      ref={drop}
      bg={isOver ? 'blue.700' : 'gray.800'}
      borderRadius="md"
      p={2}
      boxShadow={isOver ? '0 0 0 3px #3182ce' : undefined}
      transition="background 0.2s, box-shadow 0.2s"
    >
      <Text fontWeight="bold" mb={2}>Bullpen</Text>
      <Box overflowX="auto">
        <Table size="sm" variant="simple" colorScheme="gray" minW="520px">
          <Thead>
            <Tr>
              <Th>Name</Th>
              <Th textAlign="center">Type</Th>
              <Th textAlign="center">Throws</Th>
              <Th>G</Th>
              <Th>GS</Th>
              <Th>IP</Th>
              <Th>LW</Th>
              <Th></Th>
              <Th textAlign="center" w="48px">SP</Th>
              <Th textAlign="center" w="48px">SU</Th>
              <Th textAlign="center" w="48px">CL</Th>
            </Tr>
          </Thead>
          <Tbody>
            {sorted.map((p: any, idx: number) => (
              <UnassignedPitcherRow key={p.roster_player_id ?? (p as { id?: number }).id ?? `row-${idx}`} pitcher={p} idx={idx} onAssign={onAssign} lineupEditsDisabled={lineupEditsDisabled} />
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
}

function MinorLeaguePitcherRow({ pitcher }: { pitcher: any }) {
  const last = pitcher.lastName || pitcher.last_name || (pitcher.name || pitcher.full_name || '').split(' ').slice(-1)[0];
  const first = pitcher.firstName || pitcher.first_name || (pitcher.name || pitcher.full_name || '').split(' ').slice(0, -1).join(' ');
  const displayName = last && first ? `${last}, ${first}` : (pitcher.name || pitcher.full_name || last || '');
  const type = (Array.isArray(pitcher.positions) && pitcher.positions.length > 0) ? pitcher.positions[0] : (pitcher.position_1 || '');
  const throws = pitcher.throws || '';
  const resting = pitcher.status_until ? new Date(pitcher.status_until) : '';

  return (
    <Tr style={{ fontStyle: pitcher?.status_until ? 'italic' : undefined }}>
      <Td color={pitcher?.status_until ? 'gray.400' : undefined}>
        <Box>
          {displayName}
          {resting && (
            <Text fontSize="xs" color="gray.400" display="block">
              Avail: {(() => {
                return `${resting.getMonth() + 1}/${resting.getDate()}`;
              })()}
            </Text>
          )}
        </Box>
      </Td>
      <Td textAlign="center" color={pitcher?.status_until ? 'gray.400' : undefined}>{type}</Td>
      <Td textAlign="center" color={pitcher?.status_until ? 'gray.400' : undefined}>{throws}</Td>
      <Td color={pitcher?.status_until ? 'gray.400' : undefined}>{pitcher.stats?.G ?? pitcher.G ?? ''}</Td>
      <Td color={pitcher?.status_until ? 'gray.400' : undefined}>{pitcher.stats?.GS ?? pitcher.GS ?? ''}</Td>
      <Td color={pitcher?.status_until ? 'gray.400' : undefined}>{pitcher.stats?.IP ?? pitcher.IP ?? ''}</Td>
      <Td color={pitcher?.status_until ? 'gray.400' : undefined}>{pitcher.stats?.LW ?? pitcher.LW ?? ''}</Td>
      <Td color={pitcher?.status_until ? 'gray.400' : undefined}>{pitcher.stats?.Rest ?? pitcher.Rest ?? ''}</Td>
    </Tr>
  );
}

function MinorLeaguePitchersPanel({ pitchers }: { pitchers: any[] }) {
  const sorted = [...pitchers].sort((a, b) => {
    const aLast = (a.lastName || a.last_name || a.name || a.full_name || '').toLowerCase();
    const bLast = (b.lastName || b.last_name || b.name || b.full_name || '').toLowerCase();
    return aLast.localeCompare(bLast);
  });

  return (
    <Box bg="gray.800" borderRadius="md" p={2}>
      <Text fontWeight="bold" mb={2}>Minor League Pitchers</Text>
      <Box overflowX="auto">
        <Table size="sm" variant="simple" colorScheme="gray" minW="520px">
          <Thead>
            <Tr>
              <Th>Name</Th>
              <Th textAlign="center">Type</Th>
              <Th textAlign="center">Throws</Th>
              <Th>G</Th>
              <Th>GS</Th>
              <Th>IP</Th>
              <Th>LW</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {sorted.map((p: any, idx: number) => (
              <MinorLeaguePitcherRow key={p.roster_player_id ?? idx} pitcher={p} />
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
}

const darkBg = '#181A20';

export default function PitcherManagement({ teamId }: { teamId: string }) {
  const { rotation, simEvents, updateRotation, refreshRotation, isLoading } = useTeamPitchingRotation(teamId);
  const myLeague = useMyLeagueStore((state) => state.myLeague);
  const myTeam = useUserStore((s) => s.user?.user_data?.myTeam);
  const isAdminUser = isAdmin();
  const lineupLockedByLeague = isLineupEditingDisabled(myLeague);
  const adminEditingOtherTeam =
    isAdminUser &&
    myTeam != null &&
    Boolean(teamId) &&
    Number(teamId) !== Number(myTeam);
  const lineupEditsDisabled = lineupLockedByLeague || adminEditingOtherTeam;
  const toast = useToast();
  const simMap = useMemo(
    () => buildPitcherSimMap((simEvents as PitcherSimEvent[]) || []),
    [simEvents]
  );
  const [sp, setSP] = useState<PlayerType[]>([]);
  const [rp, setRP] = useState<PlayerType[]>([]);
  const [cp, setCP] = useState<PlayerType[]>([]);
  const [unassigned, setUnassigned] = useState<PlayerType[]>([]);
  const [draggedRole, setDraggedRole] = useState<string | null>(null);
  const [backendReady, setBackendReady] = useState(false);
  const latestListsRef = React.useRef<{ sp: PlayerType[]; rp: PlayerType[]; cp: PlayerType[] }>({ sp, rp, cp });

  // Minor leaguers state
  const [minorLeaguePitchers, setMinorLeaguePitchers] = useState<any[]>([]);
  const [minorLeaguersLoaded, setMinorLeaguersLoaded] = useState(false);
  const [isLoadingMinorLeaguers, setIsLoadingMinorLeaguers] = useState(false);

  const getApiErrorMessage = useCallback((err: unknown): string => {
    if (err && typeof err === 'object') {
      const maybeErr = err as { message?: unknown; error?: unknown };
      if (typeof maybeErr.message === 'string' && maybeErr.message.trim()) return maybeErr.message;
      if (typeof maybeErr.error === 'string' && maybeErr.error.trim()) return maybeErr.error;
    }
    return 'Failed to update pitching rotation';
  }, []);

  const commitRotationUpdate = useCallback((
    entries: Array<{ roster_player_id: number | undefined; role: string; order: number }>,
    previousState?: { sp: PlayerType[]; rp: PlayerType[]; cp: PlayerType[]; unassigned: PlayerType[] }
  ) => {
    const rollbackState = previousState ?? {
      sp: [...sp],
      rp: [...rp],
      cp: [...cp],
      unassigned: [...unassigned],
    };

    updateRotation(entries as any, {
      onError: (err: unknown) => {
        setSP(rollbackState.sp);
        setRP(rollbackState.rp);
        setCP(rollbackState.cp);
        setUnassigned(rollbackState.unassigned);
        latestListsRef.current = {
          sp: rollbackState.sp,
          rp: rollbackState.rp,
          cp: rollbackState.cp,
        };
        toast({
          title: 'Pitching rotation update failed',
          description: getApiErrorMessage(err),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        refreshRotation();
      }
    });
  }, [cp, getApiErrorMessage, refreshRotation, rp, sp, toast, unassigned, updateRotation]);

  useEffect(() => {
    latestListsRef.current = { sp, rp, cp };
  }, [sp, rp, cp]);

  // Initialize backend
  useEffect(() => {
    setBackendReady(true);
    return () => {
      setBackendReady(false);
    };
  }, []);

  useEffect(() => {
    if (
      rotation &&
      typeof rotation === 'object' &&
      'starters' in rotation &&
      'relievers' in rotation &&
      'closers' in rotation &&
      'unused' in rotation
    ) {
      const startersWithStats = (rotation.starters as PlayerType[]).map(p => attachSimStats(p, simMap));
      const relieversWithStats = (rotation.relievers as PlayerType[]).map(p => attachSimStats(p, simMap));
      const closersWithStats = (rotation.closers as PlayerType[]).map(p => attachSimStats(p, simMap));
      const unusedWithStats = ((rotation.unused as PlayerType[]) || []).map(p => attachSimStats(p, simMap));

      setSP(startersWithStats);
      setRP(relieversWithStats);
      setCP(closersWithStats);
      setUnassigned(unusedWithStats);
    }
  }, [rotation, simMap]);

  // Reset minor leaguers when team changes
  useEffect(() => {
    setMinorLeaguersLoaded(false);
    setMinorLeaguePitchers([]);
  }, [teamId]);

  const isPitcherPosition = (pos: string) => {
    const norm = pos?.toUpperCase?.().trim();
    return norm === 'P' || norm === 'SP' || norm === 'RP' || norm === 'CP' || norm === 'CL';
  };

  const onLoadMinorLeaguers = useCallback(async () => {
    if (!teamId) return;
    setIsLoadingMinorLeaguers(true);
    try {
      const data = await fetchTeamExtendedBench(teamId, 'pitchers');
      const extended = (data.extendedBench || []) as any[];
      // Build a sim map from the minor-league pitcher sim events returned by this endpoint
      const minorSimMap = buildPitcherSimMap((data.simEvents || []) as PitcherSimEvent[]);
      // Filter to pitchers only
      console.log(extended)
      const pitchers = extended.filter((p: any) => {
        const positions = Array.isArray(p.positions) ? p.positions : [];
        const pos1 = p.position_1 || '';
        const primary = Array.isArray(p.primaryPosition) ? p.primaryPosition : [];
        return positions.some(isPitcherPosition) || isPitcherPosition(pos1) || primary.some(isPitcherPosition);
      });
      if (pitchers.length === 0) {
        toast({
          title: 'No minor league pitchers',
          description: 'No minor-league pitchers were found on your roster.',
          status: 'info',
          duration: 4000,
          isClosable: true,
        });
        setMinorLeaguersLoaded(true);
        return;
      }
      // Attach sim stats using the minor-league sim map (same approach as getTeamPitchingRotation)
      const pitchersWithStats = pitchers.map((p: any) => attachSimStats(p, minorSimMap));
      setMinorLeaguePitchers(pitchersWithStats);
      setMinorLeaguersLoaded(true);
    } catch (err: unknown) {
      toast({
        title: 'Failed to load minor leaguers',
        description: getApiErrorMessage(err),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoadingMinorLeaguers(false);
    }
  }, [teamId, toast, getApiErrorMessage]);

  // Helper to build rotation entries for backend
  const getRosterId = (p: any) => p?.roster_player_id ?? (p as { id?: number })?.id;
  const findIndexByPitcher = (list: PlayerType[], pitcher: PlayerType) =>
    list.findIndex((p) => getRosterId(p) === getRosterId(pitcher));
  const buildRotationEntries = (spArr: PlayerType[], rpArr: PlayerType[], cpArr: PlayerType[]) => [
    ...spArr.map((p, idx) => ({ roster_player_id: getRosterId(p), role: 'SP', order: idx })),
    ...rpArr.map((p, idx) => ({ roster_player_id: getRosterId(p), role: 'RP', order: idx })),
    ...cpArr.map((p, idx) => ({ roster_player_id: getRosterId(p), role: 'CP', order: idx })),
  ];

  // Move within a list (also update ref so drop handler has current order)
  const movePitcher = useCallback((fromIdx: number, toIdx: number, role: string) => {
    let list, setList;
    if (role === 'SP') { list = sp; setList = setSP; }
    else if (role === 'RP') { list = rp; setList = setRP; }
    else { list = cp; setList = setCP; }
    const updated = [...list];
    const [moved] = updated.splice(fromIdx, 1);
    updated.splice(toIdx, 0, moved);
    setList(updated);
    latestListsRef.current = role === 'SP' ? { ...latestListsRef.current, sp: updated }
      : role === 'RP' ? { ...latestListsRef.current, rp: updated }
        : { ...latestListsRef.current, cp: updated };
  }, [sp, rp, cp]);

  const getRotationEntriesForSave = useCallback(() =>
    buildRotationEntries(latestListsRef.current.sp, latestListsRef.current.rp, latestListsRef.current.cp),
    [buildRotationEntries]);

  // Move from one list to another (append to end)
  const onDropFromOther = useCallback((item: any, toRole: string) => {
    let fromList, setFromList, toList, setToList;
    if (item.role === 'SP') { fromList = sp; setFromList = setSP; }
    else if (item.role === 'RP') { fromList = rp; setFromList = setRP; }
    else { fromList = cp; setFromList = setCP; }
    if (toRole === 'SP') { toList = sp; setToList = setSP; }
    else if (toRole === 'RP') { toList = rp; setToList = setRP; }
    else { toList = cp; setToList = setCP; }
    const fromIdx = findIndexByPitcher(fromList, item.pitcher);
    if (fromIdx < 0) return;
    const updatedFrom = [...fromList];
    const [moved] = updatedFrom.splice(fromIdx, 1);
    const updatedTo = [...toList, moved];
    setFromList(updatedFrom);
    setToList(updatedTo);
    const previousState = { sp: [...sp], rp: [...rp], cp: [...cp], unassigned: [...unassigned] };
    commitRotationUpdate(buildRotationEntries(
      toRole === 'SP' ? updatedTo : (item.role === 'SP' ? updatedFrom : sp),
      toRole === 'RP' ? updatedTo : (item.role === 'RP' ? updatedFrom : rp),
      toRole === 'CP' ? updatedTo : (item.role === 'CP' ? updatedFrom : cp)
    ), previousState);
  }, [commitRotationUpdate, sp, rp, cp, unassigned]);

  // Insert dragged pitcher at a specific index (for drop-on-row from another panel or unassigned)
  const onInsertAt = useCallback((item: any, atIdx: number, toRole: string) => {
    const fromRole = item.role;
    const toList = toRole === 'SP' ? sp : toRole === 'RP' ? rp : cp;
    const setToList = toRole === 'SP' ? setSP : toRole === 'RP' ? setRP : setCP;

    if (fromRole === 'unassigned') {
      const pitcher = item.pitcher;
      const unassignedIdx = findIndexByPitcher(unassigned, pitcher);
      if (unassignedIdx < 0) return;
      setUnassigned(unassigned.filter((_, i) => i !== unassignedIdx));
      const updatedTo = [...toList];
      updatedTo.splice(atIdx, 0, pitcher);
      setToList(updatedTo);
      const previousState = { sp: [...sp], rp: [...rp], cp: [...cp], unassigned: [...unassigned] };
      commitRotationUpdate(buildRotationEntries(
        toRole === 'SP' ? updatedTo : sp,
        toRole === 'RP' ? updatedTo : rp,
        toRole === 'CP' ? updatedTo : cp
      ), previousState);
      return;
    }

    const fromList = fromRole === 'SP' ? sp : fromRole === 'RP' ? rp : cp;
    const setFromList = fromRole === 'SP' ? setSP : fromRole === 'RP' ? setRP : setCP;
    const fromIdx = findIndexByPitcher(fromList, item.pitcher);
    if (fromIdx < 0) return;
    const updatedFrom = [...fromList];
    const [moved] = updatedFrom.splice(fromIdx, 1);
    if (fromRole === toRole) {
      const insertIdx = atIdx > fromIdx ? atIdx - 1 : atIdx;
      const updatedTo = [...updatedFrom];
      updatedTo.splice(insertIdx, 0, moved);
      setFromList(updatedTo);
      const previousState = { sp: [...sp], rp: [...rp], cp: [...cp], unassigned: [...unassigned] };
      commitRotationUpdate(buildRotationEntries(
        fromRole === 'SP' ? updatedTo : sp,
        fromRole === 'RP' ? updatedTo : rp,
        fromRole === 'CP' ? updatedTo : cp
      ), previousState);
    } else {
      const updatedTo = [...toList];
      updatedTo.splice(atIdx, 0, moved);
      setFromList(updatedFrom);
      setToList(updatedTo);
      const previousState = { sp: [...sp], rp: [...rp], cp: [...cp], unassigned: [...unassigned] };
      commitRotationUpdate(buildRotationEntries(
        toRole === 'SP' ? updatedTo : (fromRole === 'SP' ? updatedFrom : sp),
        toRole === 'RP' ? updatedTo : (fromRole === 'RP' ? updatedFrom : rp),
        toRole === 'CP' ? updatedTo : (fromRole === 'CP' ? updatedFrom : cp)
      ), previousState);
    }
  }, [commitRotationUpdate, sp, rp, cp, unassigned]);

  // Remove to unassigned
  const handleRemove = useCallback((idx: number, fromRole: string) => {
    let fromList, setFromList;
    if (fromRole === 'SP') { fromList = sp; setFromList = setSP; }
    else if (fromRole === 'RP') { fromList = rp; setFromList = setRP; }
    else { fromList = cp; setFromList = setCP; }
    const updatedFrom = [...fromList];
    const [removed] = updatedFrom.splice(idx, 1);
    setFromList(updatedFrom);
    if (removed) {
      setUnassigned([...unassigned, removed]);
    }
    const previousState = { sp: [...sp], rp: [...rp], cp: [...cp], unassigned: [...unassigned] };
    commitRotationUpdate(buildRotationEntries(
      fromRole === 'SP' ? updatedFrom : sp,
      fromRole === 'RP' ? updatedFrom : rp,
      fromRole === 'CP' ? updatedFrom : cp
    ), previousState);
  }, [commitRotationUpdate, sp, rp, cp, unassigned]);

  // Handler for dropping from unassigned to a panel
  const handleDropFromUnassigned = (pitcher: PlayerType, toRole: string) => {
    const previousState = { sp: [...sp], rp: [...rp], cp: [...cp], unassigned: [...unassigned] };
    setUnassigned(unassigned.filter(p => getRosterId(p) !== getRosterId(pitcher)));
    if (toRole === 'SP') setSP([...sp, pitcher]);
    else if (toRole === 'RP') setRP([...rp, pitcher]);
    else if (toRole === 'CP') setCP([...cp, pitcher]);
    setTimeout(() => {
      commitRotationUpdate(buildRotationEntries(
        toRole === 'SP' ? [...sp, pitcher] : sp,
        toRole === 'RP' ? [...rp, pitcher] : rp,
        toRole === 'CP' ? [...cp, pitcher] : cp
      ), previousState);
    }, 0);
  };

  // Handler for dropping from a top panel to unassigned
  const handleDropToUnassigned = (item: any) => {
    const previousState = { sp: [...sp], rp: [...rp], cp: [...cp], unassigned: [...unassigned] };
    let fromList, setFromList;
    if (item.role === 'SP') { fromList = sp; setFromList = setSP; }
    else if (item.role === 'RP') { fromList = rp; setFromList = setRP; }
    else if (item.role === 'CP') { fromList = cp; setFromList = setCP; }
    else { return; }
    const fromIdx = findIndexByPitcher(fromList, item.pitcher);
    if (fromIdx < 0) return;
    const updatedFrom = [...fromList];
    const [removed] = updatedFrom.splice(fromIdx, 1);
    setFromList(updatedFrom);
    if (removed) {
      setUnassigned([...unassigned, removed]);
    }
    commitRotationUpdate(buildRotationEntries(
      item.role === 'SP' ? updatedFrom : sp,
      item.role === 'RP' ? updatedFrom : rp,
      item.role === 'CP' ? updatedFrom : cp
    ), previousState);
  };

  return (
    <Box bg={darkBg} color="white" p={4} borderRadius="lg" overflow="hidden">
      {adminEditingOtherTeam && (
        <Flex align="center" gap={2} mb={4} color="red.400">
          <Icon as={WarningTwoIcon} color="red.400" boxSize={5} flexShrink={0} aria-hidden />
          <Text fontWeight="semibold">You do not have permission to edit this lineup</Text>
        </Flex>
      )}
      {lineupLockedByLeague && (
        <Flex align="center" gap={2} mb={4} color="red.400">
          <Icon as={WarningTwoIcon} color="red.400" boxSize={5} flexShrink={0} aria-hidden />
          <Text fontWeight="semibold">Lineups are currently locked.</Text>
        </Flex>
      )}
      {/* Top: Three columns on xl+, stack on smaller screens to preserve space for the X buttons */}
      <Flex mb={6} direction={{ base: 'column', xl: 'row' }} wrap="wrap">
        <PitcherPanel
          title="Starters"
          pitchers={sp}
          role="SP"
          gamesKey="GS"
          movePitcher={movePitcher}
          onDropFromOther={onDropFromOther}
          onInsertAt={onInsertAt}
          onRemove={handleRemove}
          onDropFromUnassigned={handleDropFromUnassigned}
          draggedRole={draggedRole}
          setDraggedRole={setDraggedRole}
          updateRotation={commitRotationUpdate}
          getRotationEntriesForSave={getRotationEntriesForSave}
          sp={sp}
          rp={rp}
          cp={cp}
          buildRotationEntries={buildRotationEntries}
          lineupEditsDisabled={lineupEditsDisabled}
        />
        <PitcherPanel
          title="Setup"
          pitchers={rp}
          role="RP"
          gamesKey="G"
          movePitcher={movePitcher}
          onDropFromOther={onDropFromOther}
          onInsertAt={onInsertAt}
          onRemove={handleRemove}
          onDropFromUnassigned={handleDropFromUnassigned}
          draggedRole={draggedRole}
          setDraggedRole={setDraggedRole}
          updateRotation={commitRotationUpdate}
          getRotationEntriesForSave={getRotationEntriesForSave}
          sp={sp}
          rp={rp}
          cp={cp}
          buildRotationEntries={buildRotationEntries}
          lineupEditsDisabled={lineupEditsDisabled}
        />
        <PitcherPanel
          title="Closers"
          pitchers={cp}
          role="CP"
          gamesKey="G"
          movePitcher={movePitcher}
          onDropFromOther={onDropFromOther}
          onInsertAt={onInsertAt}
          onRemove={handleRemove}
          onDropFromUnassigned={handleDropFromUnassigned}
          draggedRole={draggedRole}
          setDraggedRole={setDraggedRole}
          updateRotation={commitRotationUpdate}
          getRotationEntriesForSave={getRotationEntriesForSave}
          sp={sp}
          rp={rp}
          cp={cp}
          buildRotationEntries={buildRotationEntries}
          lineupEditsDisabled={lineupEditsDisabled}
        />
      </Flex>
      {/* Bottom: Full width bullpen panel */}
      <VStack spacing={4} align="stretch">
        <UnassignedPitchersPanel
          unassigned={unassigned}
          onAssign={handleDropFromUnassigned}
          onDropFromPanel={handleDropToUnassigned}
          lineupEditsDisabled={lineupEditsDisabled}
        />
        {!minorLeaguersLoaded && (
          <Flex justify="center" mt={4}>
            <Button
              colorScheme="blue"
              variant="outline"
              onClick={onLoadMinorLeaguers}
              isLoading={isLoadingMinorLeaguers}
            >
              Show Minor Leaguers
            </Button>
          </Flex>
        )}
        {minorLeaguePitchers.length > 0 && (
          <MinorLeaguePitchersPanel pitchers={minorLeaguePitchers} />
        )}
      </VStack>
    </Box>
  );
} 